import { getToken } from "../../../utils/session";
import { toWebSocketUrl } from "../../../utils/socket/webSocketUrl";

let socket = null;
let activeUsername = "";
let connectionOptions = null;
let reconnectTimer = null;
let reconnectAttempt = 0;
let pingTimer = null;
let openedOnce = false;
const pendingMessages = [];
const pendingRequests = new Map();
let requestSequence = 0;

const getWsBaseUrl = () => process.env.REACT_APP_WS_URL || "";
const isDebugEnabled = () => process.env.REACT_APP_WS_DEBUG !== "false";

const redactToken = (url) => {
  if (!url) return "";

  try {
    const nextUrl = new URL(url);

    if (nextUrl.searchParams.has("token")) {
      nextUrl.searchParams.set("token", "[redacted]");
    }

    return nextUrl.toString();
  } catch {
    return url.replace(/token=[^&]+/, "token=[redacted]");
  }
};

const getReadyStateName = (readyState) =>
  ({
    [WebSocket.CONNECTING]: "CONNECTING",
    [WebSocket.OPEN]: "OPEN",
    [WebSocket.CLOSING]: "CLOSING",
    [WebSocket.CLOSED]: "CLOSED",
  })[readyState] || "UNKNOWN";

const log = (level, message, details) => {
  if (!isDebugEnabled()) return;

  const payload = details || {};

  if (level === "error") {
    console.error(message, payload);
    return;
  }

  if (level === "warn") {
    console.warn(message, payload);
    return;
  }

  console.info(message, payload);
};

// NOTE: the backend chat channel is a RAW FastAPI WebSocket (RFC 6455) at
// /ws/chat/{username}?token=... — matching the Android client. Use the browser-native
// `WebSocket` only. Do NOT reintroduce socket.io-client / `io(...)`: Socket.IO has its
// own Engine.IO handshake/framing the server does not speak, so it will never connect.
const buildChatWsUrl = ({ username, token }) => {
  const base = toWebSocketUrl(getWsBaseUrl());
  if (!base || !username || !token) return "";

  const params = new URLSearchParams({ token });
  return `${base}/ws/chat/${encodeURIComponent(username)}?${params.toString()}`;
};

const parseSocketMessage = (event, username) => {
  log("info", "[chat-ws] raw message", {
    username,
    data: event.data,
  });

  try {
    const data = JSON.parse(event.data);

    if (data?.errors === true || data?.error) {
      log("warn", "[chat-ws] backend returned error payload", {
        username,
        data,
        note: "Socket opened, so this is a backend/application payload, not a browser connection failure.",
      });
    } else {
      log("info", "[chat-ws] parsed message", {
        username,
        data,
      });
    }

    return data;
  } catch (error) {
    log("warn", "[chat-ws] non-json message", {
      username,
      data: event.data,
      parseError: error?.message,
    });

    return { content: event.data };
  }
};

const rejectPendingMessages = (error) => {
  while (pendingMessages.length) {
    const pendingMessage = pendingMessages.shift();
    pendingMessage?.reject?.(error);
  }
};

const clearPendingRequest = (requestId) => {
  const pendingRequest = pendingRequests.get(requestId);

  if (!pendingRequest) {
    return null;
  }

  clearTimeout(pendingRequest.timeoutId);
  pendingRequests.delete(requestId);
  return pendingRequest;
};

const rejectPendingRequests = (error) => {
  for (const requestId of Array.from(pendingRequests.keys())) {
    clearPendingRequest(requestId)?.reject(error);
  }
};

const settlePendingRequest = (frame) => {
  const requestId = frame?.request_id;

  if (!requestId) {
    return;
  }

  const pendingRequest = pendingRequests.get(String(requestId));

  if (!pendingRequest) {
    return;
  }

  const frameType = String(frame?.type || "").toLowerCase();
  const isError = frame?.ok === false || Boolean(frame?.error);

  if (!isError && frameType !== pendingRequest.expectedType) {
    return;
  }

  clearPendingRequest(String(requestId));

  if (isError) {
    const message =
      frame?.error?.message || frame?.message || "Chat request failed.";
    pendingRequest.reject(new Error(message));
    return;
  }

  pendingRequest.resolve(frame);
};

const sendSerializedMessage = (serializedPayload) => {
  socket.send(serializedPayload);
};

const flushPendingMessages = () => {
  while (pendingMessages.length && socket?.readyState === WebSocket.OPEN) {
    const pendingMessage = pendingMessages.shift();

    try {
      sendSerializedMessage(pendingMessage.serializedPayload);
      pendingMessage.resolve(pendingMessage.payload);
    } catch (error) {
      pendingMessage.reject(error);
    }
  }
};

const getSocketNotReadyError = () =>
  new Error(
    "Chat socket is not connected. Open the chat page and wait for the WebSocket connection.",
  );

const clearConnectionTimers = () => {
  clearTimeout(reconnectTimer);
  clearInterval(pingTimer);
  reconnectTimer = null;
  pingTimer = null;
};

const openChatSocket = () => {
  if (!connectionOptions) return null;
  const { username } = connectionOptions;
  const token = getToken();
  const url = buildChatWsUrl({ username, token });

  if (!url) {
    log("warn", "[chat-ws] skipped connect: missing config", {
      hasBaseUrl: Boolean(getWsBaseUrl()),
      hasUsername: Boolean(username),
      hasToken: Boolean(token),
      baseUrl: getWsBaseUrl(),
    });
    return null;
  }

  log("info", "[chat-ws] connecting", {
    username,
    url: redactToken(url),
    hasToken: Boolean(token),
  });

  const nextSocket = new WebSocket(url);
  socket = nextSocket;

  nextSocket.addEventListener("open", () => {
    if (socket !== nextSocket) return;
    clearConnectionTimers();
    reconnectAttempt = 0;
    const reconnected = openedOnce;
    openedOnce = true;
    log("info", `[chat-ws] open user=${username} endpoint=${getWsBaseUrl()}`, {
      username,
      readyState: getReadyStateName(socket.readyState),
    });
    // The backend accepts JSON and ignores empty message payloads. Keep idle
    // proxy connections alive without generating conversation messages.
    pingTimer = setInterval(() => {
      if (nextSocket.readyState === WebSocket.OPEN) {
        nextSocket.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);
    flushPendingMessages();
    connectionOptions?.onOpen?.({ reconnected });
  });

  nextSocket.addEventListener("message", (event) => {
    if (socket !== nextSocket || event.data === "pong") return;
    const frame = parseSocketMessage(event, username);
    const payload = frame?.message || frame;
    log(
      "info",
      `[chat-ws] received type=${frame?.type || "none"} chat=${payload?.chat_id ?? "none"} id=${payload?.id ?? "none"}`,
    );
    settlePendingRequest(frame);
    connectionOptions?.onMessage?.(frame);
  });

  nextSocket.addEventListener("close", (event) => {
    if (socket !== nextSocket) return;
    socket = null;
    clearConnectionTimers();
    log(
      event.wasClean ? "info" : "warn",
      `[chat-ws] close user=${username} code=${event.code} reason=${event.reason || "none"}`,
      {
        username,
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        readyState: getReadyStateName(socket?.readyState),
      },
    );
    rejectPendingMessages(
      new Error(
        event.reason ||
          `Chat socket closed before pending messages were sent (${event.code})`,
      ),
    );
    rejectPendingRequests(
      new Error(
        event.reason ||
          `Chat socket closed before the server confirmed the request (${event.code})`,
      ),
    );
    if (connectionOptions && event.code !== 1008) {
      const delay = Math.min(1000 * 2 ** reconnectAttempt++, 30000);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        openChatSocket();
      }, delay);
    }
    connectionOptions?.onClose?.(event);
  });

  nextSocket.addEventListener("error", (event) => {
    if (socket !== nextSocket) return;
    log("error", "[chat-ws] browser socket error", {
      username,
      url: redactToken(url),
      readyState: getReadyStateName(socket?.readyState),
      event,
      note: "Browser WebSocket error events hide HTTP details. Check Network > WS for handshake status.",
    });
  });

  return nextSocket;
};

export const connectChatSocket = (options) => {
  const { username } = options;
  if (!buildChatWsUrl({ username, token: getToken() })) return null;
  if (
    activeUsername === username &&
    (socket?.readyState === WebSocket.OPEN ||
      socket?.readyState === WebSocket.CONNECTING ||
      reconnectTimer)
  ) {
    connectionOptions = options;
    return socket;
  }
  closeChatSocket();
  activeUsername = username;
  connectionOptions = options;
  return openChatSocket();
};

export const isChatSocketOpen = () => socket?.readyState === WebSocket.OPEN;

export const sendChatSocketMessage = (payload) =>
  new Promise((resolve, reject) => {
    if (!payload) {
      reject(new Error("Chat socket message payload is required."));
      return;
    }

    let serializedPayload = "";

    try {
      serializedPayload =
        typeof payload === "string" ? payload : JSON.stringify(payload);
    } catch (error) {
      reject(error);
      return;
    }

    if (socket?.readyState === WebSocket.OPEN) {
      try {
        log("info", "[chat-ws] send", { payload });
        sendSerializedMessage(serializedPayload);
        resolve(payload);
      } catch (error) {
        reject(error);
      }
      return;
    }

    if (socket?.readyState === WebSocket.CONNECTING) {
      log("info", "[chat-ws] queue send until open", { payload });
      pendingMessages.push({
        payload,
        serializedPayload,
        resolve,
        reject,
      });
      return;
    }

    reject(getSocketNotReadyError());
  });

export const sendChatSocketRequest = (
  payload,
  { expectedType, timeoutMs = 10000 } = {},
) => {
  if (!payload || !expectedType) {
    return Promise.reject(
      new Error("Chat socket request payload and expectedType are required."),
    );
  }

  // Mutating requests require an immediate round-trip acknowledgment. Do not
  // queue them while CONNECTING: a timed-out queued delete could otherwise be
  // sent later and mutate the server after the UI already reported failure.
  if (socket?.readyState !== WebSocket.OPEN) {
    return Promise.reject(getSocketNotReadyError());
  }

  requestSequence += 1;
  const requestId = String(
    payload.request_id || `web-${Date.now()}-${requestSequence}`,
  );
  const nextPayload = { ...payload, request_id: requestId };

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const pendingRequest = clearPendingRequest(requestId);

      pendingRequest?.reject(
        new Error("The server did not confirm the chat request in time."),
      );
    }, timeoutMs);

    pendingRequests.set(requestId, {
      expectedType: String(expectedType).toLowerCase(),
      reject,
      resolve,
      timeoutId,
    });

    sendChatSocketMessage(nextPayload).catch((error) => {
      const pendingRequest = clearPendingRequest(requestId);
      pendingRequest?.reject(error);
    });
  });
};

export const closeChatSocket = () => {
  const previousSocket = socket;
  connectionOptions = null;
  socket = null;
  clearConnectionTimers();
  openedOnce = false;
  reconnectAttempt = 0;
  if (previousSocket) {
    log("info", "[chat-ws] manual close", {
      username: activeUsername,
      readyState: getReadyStateName(previousSocket.readyState),
    });
    previousSocket.close();
  }

  rejectPendingMessages(getSocketNotReadyError());
  rejectPendingRequests(getSocketNotReadyError());
  socket = null;
  activeUsername = "";
};

const ChatSocketService = {
  connect: connectChatSocket,
  isOpen: isChatSocketOpen,
  sendMessage: sendChatSocketMessage,
  sendRequest: sendChatSocketRequest,
  close: closeChatSocket,
};

export default ChatSocketService;
